import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
  const location = useLocation();
  const navigate  = useNavigate();
  const { isAuthenticated, user, logout, isAdmin, isOrganisateur } = useAuth();
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const drawerRef = useRef(null);

  const isActive = (p) => location.pathname.startsWith(p);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Scroll shadow
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 6);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Click outside closes drawer
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target) &&
          !e.target.closest('.radar-burger')) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const links = [
    { to: '/territoire',      label: 'Les Territoires' },
    { to: '/LancerVotreRadar',          label: 'Lancer votre RADAR' },
    { to: '/qui-sommes-nous',  label: 'Qui sommes-nous ?' },
  ];

  const gradientTextStyle = {
    background: 'var(--radar-gradient)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const badgeStyle = {
    background: 'var(--radar-gradient)',
    color: '#ffffffff',
    padding: '6px 14px',
    borderRadius: 3,
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    boxShadow: '0 2px 8px rgba(255,107,0,.3)',
    textDecoration: 'none',
    display: 'inline-block',
  };

  return (
    <>
      {/* ── Bar ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        height: 'var(--nav-h)',
        background: 'rgba(255,248,242,.97)',
        backdropFilter: 'blur(12px)',
        borderBottom: scrolled ? '1px solid rgba(255,107,0,.18)' : '1px solid rgba(255,107,0,.08)',
        boxShadow: scrolled ? '0 2px 18px rgba(255,107,0,.08)' : 'none',
        transition: 'border-color .3s, box-shadow .3s',
        padding: '0 var(--px)',
      }}>
        <div style={{ maxWidth: 'var(--mw)', margin: '0 auto', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ width: 50, height: 50, borderRadius: '50%', padding: 2, background: 'var(--radar-gradient)', boxShadow: '0 2px 10px rgba(255,107,0,.35)', flexShrink: 0 }}>
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#1A0A00' }}>
                <img src="/radar-logo.jpg" alt="RADAR" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
              </div>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 25, letterSpacing: '.1em', ...gradientTextStyle }}>RADAR</span>
          </Link>

          {/* Desktop nav */}
          <div className="radar-desktop-nav" style={{ display: 'flex', gap: 24, alignItems: 'center', flex: 1, justifyContent: 'center' }}>
            {links.map(l => (
              <Link key={l.to} to={l.to} className="nav-link" style={{
                borderBottom: `2px solid ${isActive(l.to) ? 'var(--radar-deep)' : 'transparent'}`,
                color: isActive(l.to) ? 'var(--radar-deep)' : undefined,
                paddingBottom: 2,
              }}>{l.label}</Link>
            ))}
          </div>

          {/* Desktop auth */}
          <div className="radar-desktop-nav" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {isAuthenticated ? (
              <>
                {isAdmin && <Link to="/admin" style={badgeStyle}>Admin</Link>}
                {isOrganisateur && !isAdmin && <Link to="/dashboard" style={badgeStyle}>Dashboard</Link>}
                
                {/* Fixed Desktop Avatar */}
                <div style={{ width: 32, height: 32, borderRadius: '50%', padding: 2, background: 'var(--radar-gradient)' }}>
                  <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#1A0A00', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700, overflow: 'hidden' }}>
                    {user?.avatar ? (
                      <img 
                        src={user.avatar.startsWith('http') ? user.avatar : `${process.env.REACT_APP_API_BASE || ''}${user.avatar}`} 
                        alt={user.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    ) : (
                      user?.name?.charAt(0)?.toUpperCase() || 'U'
                    )}
                  </div>
                </div>
                
                <button onClick={handleLogout} style={{ background: 'none', border: '1px solid rgba(140,106,90,.3)', padding: '5px 10px', cursor: 'pointer', borderRadius: 3, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--radar-mid)' }}>
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/login" className="nav-link" style={{ opacity: .6 }}>Connexion</Link>
            )}
          </div>

          {/* Burger (mobile only) */}
          <button
            className={`radar-burger${open ? ' is-open' : ''}`}
            onClick={() => setOpen(v => !v)}
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={open}
            style={{ display: 'none' }}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── Overlay ── */}
      {open && <div className="radar-overlay" onClick={() => setOpen(false)} />}

      {/* ── Drawer ── */}
      <div ref={drawerRef} className={`radar-drawer${open ? ' is-open' : ''}`} role="dialog" aria-label="Navigation mobile">

        {/* Nav links */}
        {links.map(l => (
          <Link key={l.to} to={l.to}
            className={`radar-drawer-link${isActive(l.to) ? ' active-link' : ''}`}
          >{l.label}</Link>
        ))}

        <div className="radar-drawer-divider" />

        {/* Auth in drawer */}
        {isAuthenticated ? (
          <>
            {/* User info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 12px', background: 'rgba(217,79,0,.05)', borderRadius: 8, marginBottom: 10 }}>
              
              {/* Fixed Mobile Avatar */}
              <div style={{ width: 40, height: 40, borderRadius: '50%', padding: 2, background: 'var(--radar-gradient)', flexShrink: 0 }}>
                <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#1A0A00', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700, overflow: 'hidden' }}>
                  {user?.avatar ? (
                    <img 
                      src={user.avatar.startsWith('http') ? user.avatar : `${process.env.REACT_APP_API_BASE || ''}${user.avatar}`} 
                      alt={user.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  ) : (
                    user?.name?.charAt(0)?.toUpperCase() || 'U'
                  )}
                </div>
              </div>
              
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--radar-dark)' }}>{user?.name}</div>
                <div style={{ fontSize: 11, color: 'var(--radar-mid)', fontFamily: 'var(--font-mono)' }}>{user?.email}</div>
              </div>
            </div>
            {isAdmin && (
              <Link to="/admin" className="radar-drawer-link" style={{ background: 'rgba(217,79,0,.08)', color: 'var(--radar-orange)', fontWeight: 700 }}>
                Panneau Admin
              </Link>
            )}
            {isOrganisateur && !isAdmin && (
              <Link to="/dashboard" className="radar-drawer-link" style={{ background: 'rgba(217,79,0,.08)', color: 'var(--radar-orange)', fontWeight: 700 }}>
                Mon Dashboard
              </Link>
            )}
            <button onClick={handleLogout} style={{ marginTop: 8, width: '100%', padding: '13px', background: 'none', border: '1.5px solid rgba(140,106,90,.25)', borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', color: 'var(--radar-mid)', letterSpacing: '.04em', textTransform: 'uppercase' }}>
              Déconnexion
            </button>
          </>
        ) : (
          <Link to="/login" style={{ display: 'block', marginTop: 4, padding: '13px', background: 'var(--radar-gradient)', color: '#fff', borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', textAlign: 'center' }}>
            Connexion
          </Link>
        )}

        {/* GDPR footer links */}
        <div style={{ marginTop: 'auto', paddingTop: 24, borderTop: '1px solid rgba(140,106,90,.1)' }}>
          <p style={{ fontSize: 10, color: 'var(--radar-mid)', fontFamily: 'var(--font-mono)', textAlign: 'center', lineHeight: 1.8 }}>
            <Link to="/mentions-legales" style={{ color: 'var(--radar-mid)' }}>Mentions légales</Link>
            {' · '}
            <Link to="/confidentialite" style={{ color: 'var(--radar-mid)' }}>Confidentialité</Link>
            {' · '}
            <Link to="/cookies" style={{ color: 'var(--radar-mid)' }}>Cookies</Link>
          </p>
        </div>
      </div>
    </>
  );
}